import { useSearchFacetValues } from '@components/app/SearchLandingPage/Hooks/useSearchFacetValues'
import { Icon } from '@components/primitives/Icon'
import { classMerge } from '@components/utilities/classMerge'
import type { IconDefinition } from '@fortawesome/pro-regular-svg-icons'
import { faXmark } from '@fortawesome/pro-regular-svg-icons/faXmark'
import { trackEvent } from '@lib/tracking'
import { TrackingEvents } from '@lib/tracking/TrackingEvents'
import debounce from 'lodash/debounce'
import type { ChangeEvent, KeyboardEvent, MouseEvent, RefObject } from 'react'
import { useState } from 'react'
import { SUGGESTED_CONDITIONS } from './consts'

/**
 * Autocomplete component for condition search that displays matching conditions
 * as the user types and allows selection from a dropdown
 */
type ConditionAutocompleteProps = {
  focusNextInput?: () => void
  inputCondition: string
  inputRef?: RefObject<HTMLInputElement>
  isConditionSelected: boolean
  setInputCondition: (condition: string) => void
  setIsConditionSelected: (isConditionSelected: boolean) => void
}

const PLACEHOLDER_TEXT = 'Type Condition'

export const ConditionAutocomplete = ({
  focusNextInput,
  inputCondition,
  inputRef,
  setInputCondition,
  setIsConditionSelected,
}: ConditionAutocompleteProps) => {
  const [predictions, setPredictions] = useState<string[]>([])
  const [showSuggestedConditions, setShowSuggestedConditions] = useState(false)

  const searchFacetValues = useSearchFacetValues({
    attribute: 'conditions',
    setResults: setPredictions,
  })

  const handleInputClick = () => {
    if (inputCondition.trim() === '') {
      setShowSuggestedConditions(true)
    }
  }

  const inputChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
    setInputCondition(event.currentTarget.value)
    setShowSuggestedConditions(false) // Hide suggested conditions when typing

    if (event.currentTarget.value.length >= 1) {
      searchFacetValues(event.currentTarget.value)
    }
  }

  // Debounce input handling to prevent excessive search requests during typing
  const debouncedInputHandler = debounce(inputChangeHandler, 250, {
    leading: true,
    trailing: false,
  })

  const selectedItemHandler =
    (selectedItem: string) => (event: MouseEvent<HTMLLIElement>) => {
      event.preventDefault()
      trackEvent(TrackingEvents.HOME_PAGE_CONDITION_SELECTED, {
        val: selectedItem,
      })
      setInputCondition(selectedItem)
      setIsConditionSelected(true)
      setShowSuggestedConditions(false)
      focusNextInput?.()
    }

  /**
   * When a user navigates away from an "unfinished" condition search, we want to pick the first option in the list
   */
  const pickFirstOption = () => {
    const options = showSuggestedConditions
      ? SUGGESTED_CONDITIONS.map((c) => c.condition)
      : predictions
    if (options.length > 0) {
      const bestOption = options[0]
      if (bestOption !== undefined) {
        setInputCondition(bestOption)
        setIsConditionSelected(true)
        setShowSuggestedConditions(false)
        focusNextInput?.()
      }
    }
  }

  // Some people expect to be able to press enter after typing a condition to select the first option
  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    const options = showSuggestedConditions
      ? SUGGESTED_CONDITIONS.map((c) => c.condition)
      : predictions
    if (event.key === 'Enter' && options.length > 0) {
      event.preventDefault()
      pickFirstOption()
    }
  }

  // Get the items to display in the dropdown
  const dropdownItems = showSuggestedConditions
    ? SUGGESTED_CONDITIONS
    : predictions.map((p) => ({ condition: p }))

  return (
    <div className='group/condition relative'>
      {/* Wrapper to position the clear button absolutely */}
      <div className='relative w-full'>
        <input
          autoComplete='off'
          className='w-full text-ellipsis bg-transparent pr-8 text-lg font-semibold leading-[180%] placeholder:text-neutral400 focus:outline-none'
          id='search-bar-condition-input'
          onChange={debouncedInputHandler}
          onClick={handleInputClick}
          onKeyDown={handleKeyDown}
          placeholder={PLACEHOLDER_TEXT}
          ref={inputRef}
          type='text'
          value={inputCondition}
        />
        {inputCondition !== '' && (
          <Icon
            aria-label='Clear selected condition'
            className='absolute -top-2 right-1 rounded-full px-2 py-1 text-neutral900 opacity-0 hover:bg-neutral200 focus:outline-none group-focus-within/condition:opacity-100'
            icon={faXmark}
            onClick={() => {
              setInputCondition('')
              setIsConditionSelected(false)
              setPredictions([]) // Clear the predictions
              setShowSuggestedConditions(false)
            }}
            size='lg'
          />
        )}
      </div>
      {/* List of items to refine given the input search box above */}
      <ul
        className={classMerge(
          'absolute top-[36px] z-modalSearchPanel mx-auto my-4 w-full rounded-2xl border-2 border-neutral200 bg-white px-8 py-4',
          'transition-opacity duration-200',
          'pointer-events-none opacity-0 group-focus-within/condition:pointer-events-auto group-focus-within/condition:opacity-100',
          dropdownItems.length === 0 && 'hidden',
        )}
        onMouseDown={(e) => e.preventDefault()}
      >
        {showSuggestedConditions && (
          <span className='mb-2 text-sm font-normal'>Suggested Conditions</span>
        )}
        {dropdownItems.map((item, index) => (
          <DropdownItem
            className='-ml-4 flex flex-row items-center justify-between rounded-2xl hover:cursor-pointer hover:bg-neutral200'
            item={item}
            key={index}
            selectedItemHandler={selectedItemHandler}
          />
        ))}
      </ul>
    </div>
  )
}

export const DropdownItem = ({
  className,
  item,
  selectedItemHandler,
}: {
  className?: string
  item: {
    bgColor?: string
    color?: string
    condition: string
    icon?: IconDefinition
  }
  selectedItemHandler: (
    itemValue: string,
  ) => (event: MouseEvent<HTMLLIElement>) => void
}) => {
  return (
    <li
      className={className}
      key={item.condition}
      onClick={selectedItemHandler(item.condition)}
    >
      <div className={'m-0 flex items-center gap-4 p-3'}>
        {item.icon && (
          <Icon
            className={classMerge(
              'size-6 rounded-lg bg-neutral50 p-2 text-neutral600',
              item.color,
              item.bgColor,
            )}
            icon={item.icon}
          />
        )}
        <span className='text-base font-medium'>{item.condition}</span>
      </div>
    </li>
  )
}
